/**
 * Renders ROUTE_TABLE back into a react-router 5 `<Switch>` tree, reproducing
 * exactly what `components/App/App.tsx` used to inline.
 *
 * `GuardedRoute` is the shared replacement for `ProtectedRoute`: an entry with a
 * `guard` that fails redirects to `guardRedirect()` (or DEFAULT_REDIRECT) plus
 * the current search, identical to the old `ProtectedRoute` behaviour. Entries
 * whose `hidden(root)` is true are dropped from the Switch, so the path falls
 * through to the trailing redirect — matching the old
 * `{!shouldBlockEditorXSignup() && <Route/>}` wrapper.
 *
 * This file is under `src/routing/**`, allowed to import react-router directly.
 *
 * See docs/ssr-shared-source-plan.md, section 2.2.
 */
import React, { useContext } from 'react';
import { observer } from 'mobx-react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { AppContextProvider } from '../components/AppLoader';
import { DEFAULT_REDIRECT, ROUTE_TABLE, RouteEntry } from '../routeTable';

const GuardedRoute: React.FC<{ entry: RouteEntry }> = observer(({ entry }) => {
  const { rootStore } = useContext(AppContextProvider);
  const { search } = useLocation();

  if (entry.guard && !entry.guard(rootStore)) {
    const target = entry.guardRedirect ? entry.guardRedirect() : DEFAULT_REDIRECT;
    return <Redirect to={`${target}${search}`} />;
  }

  const Component = entry.component;
  return <Component />;
});

export const AppRoutes: React.FC = observer(() => {
  const { rootStore } = useContext(AppContextProvider);
  const { search } = useLocation();

  return (
    <Switch>
      {ROUTE_TABLE.filter(
        (entry) => !entry.hidden || !entry.hidden(rootStore),
      ).map((entry) => (
        <Route key={entry.id} path={entry.path} exact={entry.exact}>
          <GuardedRoute entry={entry} />
        </Route>
      ))}
      <Redirect to={`${DEFAULT_REDIRECT}${search}`} />
    </Switch>
  );
});

export default AppRoutes;
